import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listAll, removeItem, updateItem } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";
import { Plus, Pencil, Trash2, TrendingUp } from "lucide-react";

// Dedicated Proven Results / Case Studies manager (MeProperty-style list),
// themed for A Homely Abode. Stories with rich text + gallery, shown on
// /success-stories/proven-results and its detail pages.
export default function ProvenResultsList() {
  const { data: rows, loading, error, reload } = useAsyncData(() => listAll("provenResults"), []);
  const items = [...(rows || [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

  const del = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete case study "${item.headline || item.id}"? This cannot be undone.`)) return;
    try {
      await removeItem("provenResults", item.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await updateItem("provenResults", item.id, { status: next });
    reload();
  };

  return (
    <div data-testid="admin-proven-results">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow text-bronze mb-1">Content</div>
          <h1 className="font-serif text-3xl text-ink">Proven Results</h1>
          <p className="text-sm text-olive mt-1">Case studies — challenge, strategy &amp; outcome stories shown on /success-stories/proven-results.</p>
        </div>
        <Link to="/admin/proven-results/new" data-testid={ADMIN.newButton} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New case study
        </Link>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <table className="w-full text-sm border border-bronze/20 bg-cream" data-testid={ADMIN.list}>
          <thead className="bg-beige/60 text-left text-[0.65rem] uppercase tracking-wider text-olive">
            <tr>
              <th className="p-3 w-20">Image</th>
              <th className="p-3">Case study</th>
              <th className="p-3 w-44">Metric</th>
              <th className="p-3 w-24">Visibility</th>
              <th className="p-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const img = item.image || (Array.isArray(item.images) ? item.images[0] : "");
              return (
                <tr key={item.id} className="border-t border-bronze/10 align-middle" data-testid={`admin-row-${item.id}`}>
                  <td className="p-2">
                    <div className="h-12 w-14 overflow-hidden bg-beige border border-bronze/20 flex items-center justify-center">
                      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <TrendingUp size={16} className="text-bronze/50" />}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-ink">{item.headline || item.id}</div>
                    <div className="text-xs text-olive">{item.propertyType || item.sub || "—"}</div>
                  </td>
                  <td className="p-3 text-xs text-ink_soft">{item.metric || "—"}</td>
                  <td className="p-3">
                    <button
                      onClick={() => togglePublish(item)}
                      data-testid={ADMIN.publishToggle}
                      className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider ${item.status === "published" ? "bg-bronze text-cream" : "bg-taupe/40 text-ink"}`}
                    >
                      {item.status || "draft"}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/proven-results/${item.id}`} className="text-bronze hover:text-bronze_dark" title="Edit"><Pencil size={15} /></Link>
                      <button onClick={() => del(item)} data-testid={ADMIN.deleteButton} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-olive">No case studies yet — click “New case study”.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
